import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../../utils/api';
import Modal from '../../common/Modal';
import Table from '../../common/Table';
import './CategoriesView.scss';

const CategoriesView = () => {
    const jwtToken = useSelector((state) => state.auth.jwtToken);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: ''
    });
    const [openMenuId, setOpenMenuId] = useState(null);

    // Fetch categories
    const fetchCategories = async () => {
        if (!jwtToken) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await getCategories();
            if (response && response.data) {
                const fetchedCategories = response.data.data || response.data || [];
                setCategories(Array.isArray(fetchedCategories) ? fetchedCategories : []);
            } else {
                setCategories([]);
            }
        } catch (err) {
            console.error('Failed to fetch categories:', err);
            setError(err.message || 'Failed to load categories');
            setCategories([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch categories on mount
    useEffect(() => {
        if (jwtToken) {
            fetchCategories();
        }
    }, [jwtToken]);

    // Debounce search
    useEffect(() => {
        if (jwtToken) {
            const debounceTimer = setTimeout(() => {
                fetchCategories();
            }, 500);
            
            return () => clearTimeout(debounceTimer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    // Handle open modal for create
    const handleOpenCreateModal = () => {
        setEditingCategory(null);
        setFormData({
            title: '',
            description: ''
        });
        setIsModalOpen(true);
    };

    // Handle open modal for edit
    const handleOpenEditModal = (category) => {
        setEditingCategory(category);
        setFormData({
            title: category.title || category.name || '',
            description: category.description || ''
        });
        setIsModalOpen(true);
    };

    // Handle close modal
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingCategory(null);
        setFormData({
            title: '',
            description: ''
        });
    };

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!jwtToken) return;
        
        if (!formData.title.trim()) {
            alert('Please enter a category title');
            return;
        }

        try {
            if (editingCategory) {
                // Update category
                const response = await updateCategory(editingCategory.id, formData);
                if (response && response.data) {
                    await fetchCategories();
                    handleCloseModal();
                } else {
                    alert('Failed to update category. Please try again.');
                }
            } else {
                // Create category
                const response = await createCategory(formData);
                if (response && response.data) {
                    await fetchCategories();
                    handleCloseModal();
                } else {
                    alert('Failed to create category. Please try again.');
                }
            }
        } catch (err) {
            console.error('Failed to save category:', err);
            alert('Failed to save category. Please try again.');
        }
    };

    // Handle delete category
    const handleDelete = async (categoryId) => {
        if (!jwtToken) return;
        
        if (!window.confirm('Are you sure you want to delete this category?')) {
            return;
        }

        try {
            const response = await deleteCategory(categoryId);
            if (response) {
                await fetchCategories();
            } else {
                alert('Failed to delete category. Please try again.');
            }
        } catch (err) {
            console.error('Failed to delete category:', err);
            alert('Failed to delete category. Please try again.');
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openMenuId && !event.target.closest('.octopus-table-menu-wrapper')) {
                setOpenMenuId(null);
            }
        };

        if (openMenuId) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [openMenuId]);

    // Filter categories by search query
    const filteredCategories = categories.filter(category => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        const title = (category.title || category.name || '').toLowerCase();
        const description = (category.description || '').toLowerCase();
        return title.includes(searchLower) || description.includes(searchLower);
    });

    // Define table columns
    const tableColumns = [
        {
            key: 'name',
            label: 'Name',
            headerStyle: { width: '30%', minWidth: '200px' },
            render: (row) => (
                <div className="octopus-table-name-cell">
                    <div className="octopus-table-name-content">
                        <span className="octopus-table-name-text">
                            {row.title || row.name}
                        </span>
                    </div>
                </div>
            )
        },
        {
            key: 'description',
            label: 'Description',
            headerStyle: { width: '50%', minWidth: '300px' },
            render: (row) => (
                <div className="octopus-table-description">
                    {row.description || '-'}
                </div>
            )
        },
        {
            key: 'actions',
            label: '',
            headerStyle: { width: '20%', minWidth: '100px', textAlign: 'right' },
            render: (row, rowIndex) => {
                const categoryId = row.id || rowIndex;
                const isMenuOpen = openMenuId === categoryId;
                return (
                    <div className="octopus-table-row-actions">
                        <div className="octopus-table-menu-wrapper">
                            <button 
                                className="octopus-table-row-menu"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(isMenuOpen ? null : categoryId);
                                }}
                            >
                                <i className="fa-solid fa-ellipsis-v"></i>
                            </button>
                            {isMenuOpen && (
                                <div className="octopus-table-row-dropdown">
                                    <button 
                                        className="octopus-table-dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenEditModal(row);
                                            setOpenMenuId(null);
                                        }}
                                    >
                                        <i className="fa-solid fa-edit"></i>
                                        Edit
                                    </button>
                                    <button 
                                        className="octopus-table-dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(categoryId);
                                            setOpenMenuId(null);
                                        }}
                                    >
                                        <i className="fa-solid fa-trash"></i>
                                        Delete
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
        }
    ];

    return (
        <div className="octopus-projects-manager octopus-categories-manager">
            <div className="octopus-projects-manager-header">
                <h2 className="octopus-projects-manager-title">Categories</h2>
                <div className="octopus-projects-manager-actions">
                    <button 
                        className="octopus-projects-btn octopus-projects-btn-primary"
                        onClick={handleOpenCreateModal}
                    >
                        <span className="octopus-icon-plus">+</span>
                        New Category
                    </button>
                    <div className="octopus-projects-search">
                        <input
                            type="text"
                            placeholder="Search categories ..."
                            className="octopus-projects-search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Categories Content */}
            <div className="octopus-projects-content">
                {isLoading && categories.length === 0 ? (
                    <div className="octopus-projects-empty">
                        <p>Loading categories...</p>
                    </div>
                ) : error ? (
                    <div className="octopus-projects-empty">
                        <p>Error: {error}</p>
                    </div>
                ) : filteredCategories.length === 0 ? (
                    <div className="octopus-projects-empty">
                        <p>No categories found. Create a new category to get started!</p>
                    </div>
                ) : (
                    <Table
                        columns={tableColumns}
                        data={filteredCategories}
                    />
                )}
            </div>

            {/* Create/Edit Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingCategory ? 'Edit Category' : 'New Category'}
                size="medium"
                footer={
                    <>
                        <button
                            type="button"
                            className="octopus-projects-btn octopus-projects-btn-secondary"
                            onClick={handleCloseModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="category-form"
                            className="octopus-projects-btn octopus-projects-btn-primary"
                        >
                            {editingCategory ? 'Update' : 'Create'}
                        </button>
                    </>
                }
            >
                <form id="category-form" onSubmit={handleSubmit}>
                    <div className="octopus-form-group">
                        <label htmlFor="category-title">Title *</label>
                        <input
                            id="category-title"
                            type="text"
                            className="octopus-form-input"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="Enter category title"
                            required
                        />
                    </div>
                    
                    <div className="octopus-form-group">
                        <label htmlFor="category-description">Description</label>
                        <textarea
                            id="category-description"
                            className="octopus-form-textarea"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Enter category description (optional)"
                            rows="4"
                        />
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default CategoriesView;
